import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SearchBar from './SearchBar';
import { PlusCircle, Calendar, MessageSquare, Heart, Shield, FileText, RefreshCw, Monitor, Upload, Download } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const iconMap = {
  PlusCircle: PlusCircle,
  Calendar: Calendar,
  MessageSquare: MessageSquare,
  Heart: Heart,
  Shield: Shield,
  FileText: FileText,
};

// Configuration des couleurs par section
const SECTION_THEMES = {
  rdv: {
    primary: 'green',
    bgGradient: 'from-green-50 via-green-100/50 to-emerald-50',
    decorLeft: 'bg-green-200/30',
    decorRight: 'bg-emerald-200/40',
    pillActive: 'bg-green-500 shadow-green-500/30',
    titleColor: 'text-green-600',
    highlightColor: 'text-green-500',
    btnPrimary: 'bg-green-500 hover:bg-green-600 shadow-green-500/30',
    btnSecondary: 'text-green-600 border-green-200 hover:bg-green-50',
    iconBg: 'bg-green-100',
    iconColor: 'text-green-600',
    badgeBg: 'bg-orange-400',
  },
  carnet: {
    primary: 'blue',
    bgGradient: 'from-blue-50 via-sky-100/50 to-cyan-50',
    decorLeft: 'bg-blue-200/30',
    decorRight: 'bg-cyan-200/40',
    pillActive: 'bg-blue-500 shadow-blue-500/30',
    titleColor: 'text-blue-600',
    highlightColor: 'text-blue-500',
    btnPrimary: 'bg-blue-500 hover:bg-blue-600 shadow-blue-500/30',
    btnSecondary: 'text-blue-600 border-blue-200 hover:bg-blue-50',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
    badgeBg: 'bg-cyan-500',
  },
  integration: {
    primary: 'purple',
    bgGradient: 'from-purple-50 via-violet-100/50 to-indigo-50',
    decorLeft: 'bg-purple-200/30',
    decorRight: 'bg-indigo-200/40',
    pillActive: 'bg-purple-500 shadow-purple-500/30',
    titleColor: 'text-purple-600',
    highlightColor: 'text-purple-500',
    btnPrimary: 'bg-purple-500 hover:bg-purple-600 shadow-purple-500/30',
    btnSecondary: 'text-purple-600 border-purple-200 hover:bg-purple-50',
    iconBg: 'bg-purple-100',
    iconColor: 'text-purple-600',
    badgeBg: 'bg-indigo-500',
  }
};

const HeroSection = ({ onSearch, onGeolocate, isLocating }) => {
  const navigate = useNavigate();
  const [activeFeature, setActiveFeature] = useState(0);
  const [features, setFeatures] = useState([
    { id: '1', icon: 'PlusCircle', title: 'Trouvez un vétérinaire', description: 'Trouvez un vétérinaire parmi les 7000 cliniques répertoriées sur notre site internet' },
    { id: '2', icon: 'Calendar', title: 'Réservez un créneau', description: 'Sélectionnez un créneau directement pour prendre rendez-vous en ligne' },
    { id: '3', icon: 'MessageSquare', title: 'Recevez un rappel', description: 'Recevez un rappel par SMS et email la veille du rendez-vous' }
  ]);

  // Auto-rotate features
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % 3);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await fetch(`${API_URL}/api/content/homepage`);
        if (response.ok) {
          const data = await response.json();
          if (data.features && data.features.length > 0) {
            setFeatures(data.features);
          }
        }
      } catch (error) {
        console.error('Error fetching homepage content:', error);
      }
    };
    fetchContent();
  }, []);

  const heroContent = [
    {
      key: 'rdv',
      title: "Votre rendez-vous",
      subtitle: "vétérinaire",
      highlight: "en quelques clics !",
      badge: { text: "Simple, rapide et gratuit", emoji: "🐾" },
      description: "Prenez rendez-vous chez un vétérinaire en quelques clics. C'est gratuit pour les propriétaires d'animaux.",
      image: "https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=600&h=500&fit=crop",
      imageAlt: "Chien heureux"
    },
    {
      key: 'carnet',
      title: "Le carnet de santé",
      subtitle: "digital",
      highlight: "de votre animal",
      badge: { text: "Vaccins, rappels, documents", emoji: "📋" },
      description: "Suivez la santé de votre animal avec notre carnet digital : vaccins, rappels automatiques, documents partagés avec votre vétérinaire.",
      image: "https://images.unsplash.com/photo-1548199973-03cce0bbc87b?w=600&h=500&fit=crop",
      imageAlt: "Chiens en bonne santé"
    },
    {
      key: 'integration',
      title: "Synchronisez",
      subtitle: "votre logiciel",
      highlight: "vétérinaire",
      badge: { text: "Vétocom, Bourgelat...", emoji: "🔄" },
      description: "Importez et exportez vos données entre vetoRDV et votre logiciel de gestion vétérinaire. Compatible avec tous les logiciels.",
      image: "https://images.unsplash.com/photo-1576201836106-db1758fd1c97?w=600&h=500&fit=crop",
      imageAlt: "Vétérinaire professionnel"
    }
  ];

  const current = heroContent[activeFeature];
  const theme = SECTION_THEMES[current.key];

  // Features pour chaque section
  const sectionFeatures = {
    rdv: features,
    carnet: [
      { icon: 'Heart', description: 'Centralisez toutes les informations de santé de votre animal : vaccins, traitements, consultations' },
      { icon: 'Calendar', description: 'Recevez des rappels automatiques pour les vaccins et traitements de votre compagnon' },
      { icon: 'FileText', description: 'Partagez le carnet de santé avec votre vétérinaire pour un suivi optimal' }
    ],
    integration: [
      { icon: 'Upload', description: 'Importez vos clients, animaux et historiques médicaux depuis votre logiciel actuel' },
      { icon: 'Download', description: 'Exportez vos données vetoRDV au format CSV compatible avec tous les logiciels' },
      { icon: 'Monitor', description: 'Compatible Vétocom, Bourgelat, Assistovet, DrVeto, Vetup et tous les logiciels avec export CSV' }
    ]
  };

  const integrationIcons = { Upload, Download, Monitor };

  return (
    <section className="relative overflow-hidden transition-all duration-700">
      {/* Background - Change color based on section */}
      <div className={`absolute inset-0 bg-gradient-to-br ${theme.bgGradient} transition-all duration-700`}>
        {/* Decorative shapes */}
        <div className={`absolute top-20 left-10 w-32 h-32 ${theme.decorLeft} rounded-full blur-3xl transition-all duration-700`}></div>
        <div className={`absolute bottom-20 right-20 w-48 h-48 ${theme.decorRight} rounded-full blur-3xl transition-all duration-700`}></div>
        {/* Wave pattern */}
        <svg className="absolute bottom-0 left-0 right-0" viewBox="0 0 1440 120" fill="none">
          <path d="M0 120L60 110C120 100 240 80 360 75C480 70 600 80 720 85C840 90 960 90 1080 85C1200 80 1320 70 1380 65L1440 60V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
        </svg>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-32">
        {/* Main Value Proposition - Always visible */}
        <div className="text-center mb-10">
          <span className="inline-block px-4 py-1.5 bg-teal-100 text-teal-700 rounded-full text-sm font-semibold mb-4">
            🏥 Plateforme santé animale complète
          </span>
          <h2 className="text-xl md:text-2xl text-gray-700 font-medium max-w-3xl mx-auto leading-relaxed">
            VetoRDV est la <span className="text-teal-600 font-bold">première plateforme</span> qui combine 
            prise de rendez-vous et carnet médical animalier <span className="text-teal-600 font-bold">partagé entre vétérinaires</span>.
          </h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 items-center">
          {/* Left Content */}
          <div className="text-left">
            {/* Feature Toggle Pills - 3 options with different colors */}
            <div className="flex flex-wrap gap-2 mb-6">
              <button
                onClick={() => setActiveFeature(0)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  activeFeature === 0 
                    ? `${SECTION_THEMES.rdv.pillActive} text-white shadow-lg` 
                    : 'bg-white/80 text-gray-600 hover:bg-white'
                }`}
                data-testid="hero-tab-rdv"
              >
                🗓️ Rendez-vous
              </button>
              <button
                onClick={() => setActiveFeature(1)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  activeFeature === 1 
                    ? `${SECTION_THEMES.carnet.pillActive} text-white shadow-lg` 
                    : 'bg-white/80 text-gray-600 hover:bg-white'
                }`}
                data-testid="hero-tab-carnet"
              >
                📋 Carnet de santé
              </button>
              <button
                onClick={() => setActiveFeature(2)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  activeFeature === 2 
                    ? `${SECTION_THEMES.integration.pillActive} text-white shadow-lg` 
                    : 'bg-white/80 text-gray-600 hover:bg-white'
                }`}
                data-testid="hero-tab-integration"
              >
                🔄 Intégration Pro
              </button>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight transition-all duration-500">
              <span className={`${theme.titleColor} block transition-colors duration-500`}>{current.title}</span>
              <span className={`${theme.titleColor} block transition-colors duration-500`}>{current.subtitle}</span>
              <span className={`${theme.highlightColor} block transition-colors duration-500`}>{current.highlight}</span>
            </h1>

            {/* Description */}
            <p className="mt-6 text-lg text-gray-600 max-w-md transition-opacity duration-300">
              {current.description}
            </p>
            
            {/* CTA Buttons - Color changes with section */}
            <div className="mt-6 flex flex-wrap gap-3">
              {activeFeature === 0 && (
                <button 
                  onClick={() => navigate('/recherche')}
                  className={`px-6 py-3 ${theme.btnPrimary} text-white rounded-full font-medium transition-all duration-300 shadow-lg`}
                  data-testid="find-vet-button"
                >
                  Trouver un vétérinaire
                </button>
              )}
              {activeFeature === 1 && (
                <button 
                  onClick={() => navigate('/espace-patient')}
                  className={`px-6 py-3 ${theme.btnPrimary} text-white rounded-full font-medium transition-all duration-300 shadow-lg`}
                  data-testid="create-carnet-button"
                >
                  Créer mon carnet gratuit
                </button>
              )}
              {activeFeature === 2 && (
                <button 
                  onClick={() => navigate('/espace-veterinaire')}
                  className={`px-6 py-3 ${theme.btnPrimary} text-white rounded-full font-medium transition-all duration-300 shadow-lg`}
                  data-testid="discover-integration-button"
                >
                  Découvrir l'intégration
                </button>
              )}
              <button 
                onClick={() => {
                  const section = activeFeature === 2 
                    ? document.querySelector('#integration-section')
                    : document.querySelector('#carnet-sante-section');
                  if (section) section.scrollIntoView({ behavior: 'smooth' });
                }}
                className={`px-6 py-3 bg-white ${theme.btnSecondary} rounded-full font-medium transition-all duration-300 border`}
              >
                En savoir plus
              </button>
            </div>
            
            {/* Badge - Color changes */}
            <div className="mt-8 inline-flex items-center justify-center">
              <div className="relative w-28 h-28">
                <div className={`absolute inset-0 ${theme.badgeBg} rounded-full flex items-center justify-center shadow-lg transform rotate-[-15deg] transition-all duration-500 hover:rotate-0`}>
                  <div className="text-white text-center leading-tight">
                    <span className="text-2xl block mb-1">{current.badge.emoji}</span>
                    <span className="text-[10px] font-medium block px-2">{current.badge.text}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Content - Image changes with feature */}
          <div className="relative flex justify-center lg:justify-end">
            <div className="relative">
              <img
                src={current.image}
                alt={current.imageAlt}
                className="w-full max-w-md lg:max-w-lg object-cover rounded-3xl shadow-2xl transition-all duration-500"
              />
              {/* Floating card for carnet de santé */}
              {activeFeature === 1 && (
                <div className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-xl p-4 animate-fadeInUp">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 ${theme.iconBg} rounded-full flex items-center justify-center`}>
                      <Shield className={`w-5 h-5 ${theme.iconColor}`} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-800">Rappel vaccin</p>
                      <p className={`text-xs ${theme.iconColor}`}>Dans 7 jours</p>
                    </div>
                  </div>
                </div>
              )}
              {/* Floating card for integration */}
              {activeFeature === 2 && (
                <div className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-xl p-4 animate-fadeInUp">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 ${theme.iconBg} rounded-full flex items-center justify-center`}>
                      <RefreshCw className={`w-5 h-5 ${theme.iconColor}`} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-800">Synchronisation</p>
                      <p className={`text-xs ${theme.iconColor}`}>100% compatible</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div id="search-bar" className="mt-8 relative z-10">
          <SearchBar onSearch={onSearch} onGeolocate={onGeolocate} isLocating={isLocating} />
        </div>

        {/* Features - Changes based on section */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          {current.key === 'rdv' && features.map((feature, index) => {
            const IconComponent = iconMap[feature.icon];
            return (
              <div key={index} className="flex items-start gap-4">
                <div className={`flex-shrink-0 w-12 h-12 ${theme.iconBg} rounded-full flex items-center justify-center transition-colors duration-500`}>
                  {IconComponent && <IconComponent className={`w-6 h-6 ${theme.iconColor} transition-colors duration-500`} />}
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </div>
            );
          })}
          
          {current.key === 'carnet' && sectionFeatures.carnet.map((feature, index) => {
            const IconComponent = iconMap[feature.icon];
            return (
              <div key={index} className="flex items-start gap-4">
                <div className={`flex-shrink-0 w-12 h-12 ${theme.iconBg} rounded-full flex items-center justify-center transition-colors duration-500`}>
                  {IconComponent && <IconComponent className={`w-6 h-6 ${theme.iconColor} transition-colors duration-500`} />}
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </div>
            );
          })}
          
          {current.key === 'integration' && sectionFeatures.integration.map((feature, index) => {
            const IconComponent = integrationIcons[feature.icon];
            return (
              <div key={index} className="flex items-start gap-4">
                <div className={`flex-shrink-0 w-12 h-12 ${theme.iconBg} rounded-full flex items-center justify-center transition-colors duration-500`}>
                  {IconComponent && <IconComponent className={`w-6 h-6 ${theme.iconColor} transition-colors duration-500`} />}
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
